import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type { Country, CreateCountryRequest } from "../../../api/Api";

export type CountryStatusFilter = "active" | "archived" | "all";

export const countryKeys = {
  all: ["countries"] as const,
  overviews: () => [...countryKeys.all, "overview"] as const,
  overview: (status: CountryStatusFilter = "active") =>
    [...countryKeys.overviews(), status] as const,
  completedOverview: () => [...countryKeys.overviews(), "completed"] as const,
  specific: (id: string) => [...countryKeys.all, "specific", id] as const,
  assignees: (groupName: string) =>
    [...countryKeys.all, "assignees", groupName] as const
};

export const useCountryOverview = (status: CountryStatusFilter = "active") => {
  return useQuery({
    queryKey: countryKeys.overview(status),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOverview.countryOverview({
        status
      });
      return response.data;
    }
  });
};

export const useCompletedCountryOverview = () => {
  return useQuery({
    queryKey: countryKeys.completedOverview(),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response =
        await apiClient.completedCountryOverview.completedCountryOverview();
      return response.data;
    }
  });
};

export const useSpecificCountry = (
  id?: string,
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: countryKeys.specific(id ?? "not_set"),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countrySpecific.countrySpecific({
        id: id ?? "id_should_always_exist"
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !!id,
    refetchInterval: options?.refetchInterval
  });
};

export const useCountryAssignees = (groupName: string) => {
  return useQuery({
    queryKey: countryKeys.assignees(groupName),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryAssignees.countryAssignees({
        groupName
      });
      return response.data;
    },
    enabled: !!groupName
  });
};

//mutations

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useCreateCountry = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateCountryRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryCreate.countryCreate(request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useArchiveCountry = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryArchive.countryArchive({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useRestoreCountry = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryRestore.countryRestore({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error as Error);
    }
  });
};

export const useUpdateCountry = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async (country: Country) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryUpdate.countryUpdate(country);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.id) {
        //optimistic update of the specific country query data
        queryClient.setQueryData(countryKeys.specific(variables.id), variables);
        // Then refetch: the server stamps a new version on every save and merges the next save
        // against the version it carries. Keeping the one sent here would make undoing your own
        // earlier change look like no change (ADHO-496).
        queryClient.invalidateQueries({
          queryKey: countryKeys.specific(variables.id)
        });
      }

      queryClient.invalidateQueries({
        queryKey: countryKeys.overviews()
      });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error as Error);
      }
    }
  });
};
